import { DataPlaneResourcePathPrefix } from '../constants/data-plane';
import { StringifiedPattern } from '../constants/patterns';
import { ClientError } from '../models/common';
import { DataPlaneIdTokenSet } from '../models/data-plane';
import { isUndefinedOrWhiteSpace } from '../utilities/string';
import { tryOrDefault } from '../utilities/try-or-default';
import { isValidDataPlaneUri, parseDataPlanePath } from './data-plane';

/* eslint-disable security/detect-non-literal-regexp */
// Justification: regex is non-literal, but is not constructed from user input.
const poolPathPattern = new RegExp(
    `^/${DataPlaneResourcePathPrefix.Project}/${StringifiedPattern.ResourceName}/${DataPlaneResourcePathPrefix.Pool}/${StringifiedPattern.ResourceName}$`,
    'i'
);
/* eslint-enable security/detect-non-literal-regexp */

export const createPoolDataPlaneUri = (
    tokens: DataPlaneIdTokenSet<'devCenter' | 'poolName' | 'projectName'>
): string => {
    const { devCenter, poolName, projectName } = tokens;

    if (isUndefinedOrWhiteSpace(devCenter)) {
        throw new ClientError('Dev center token cannot be undefined or white space');
    }

    if (isUndefinedOrWhiteSpace(poolName)) {
        throw new ClientError('Pool name token cannot be undefined or white space');
    }

    if (isUndefinedOrWhiteSpace(projectName)) {
        throw new ClientError('Project name token cannot be undefined or white space');
    }

    const path = `/${[
        DataPlaneResourcePathPrefix.Project,
        projectName.toLowerCase(),
        DataPlaneResourcePathPrefix.Pool,
        poolName.toLowerCase(),
    ].join('/')}`;

    const uri = new URL(path, devCenter);

    return `${uri}`;
};

export const getTokensFromPoolDataPlaneUri = (
    uri: string
): DataPlaneIdTokenSet<'devCenter' | 'poolName' | 'projectName'> => {
    if (!isValidPoolDataPlaneUri(uri)) {
        throw new ClientError('Given URI was not a valid Data Plane URI for a pool');
    }

    const { origin, pathname } = new URL(uri);
    const [, projectName, , poolName] = parseDataPlanePath(pathname);

    return {
        devCenter: origin,
        poolName,
        projectName,
    };
};

export const isValidPoolDataPlaneUri = (uri: string): boolean => isValidDataPlaneUri(uri, poolPathPattern);

export const tryCreatePoolDataPlaneUri = tryOrDefault(createPoolDataPlaneUri);
export const tryGetTokensFromPoolDataPlaneUri = tryOrDefault(getTokensFromPoolDataPlaneUri);
